import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import {
    IDisbursementBatch,
    IDisbursementBatchCreateRequest,
    IEmployeeLoan,
    ILoanApproveRequest,
    ILoanOpeningBalancePreview,
    ILoanOpeningBalanceRequest,
    ILoanCreateRequest,
    ILoanInstallment,
    ILoanPage,
    ILoanRepayRequest,
    ILoanSelfRequest,
    IOutstandingLoanSummary,
    LoanStatus,
} from '../interfaces/employeeLoan.interface';

const headers = () => ({ headers: AuthService.getAuthHeader() });

/** A failed blob download carries its JSON error as a Blob; surface the message so the toast can show it. */
async function rethrowBlobError(error: any): Promise<never> {
    const data = error?.response?.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
            const text = await data.text();
            let message = text;
            try {
                const parsed = JSON.parse(text);
                if (parsed && typeof parsed.message === 'string') message = parsed.message;
            } catch {
                // not JSON: keep the raw text
            }
            if (message) error.response.data = message;
        } catch {
            // keep the original error
        }
    }
    throw error;
}

const filenameFrom = (disposition: string | undefined, fallback: string): string => {
    const match = disposition ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition) : null;
    return match ? decodeURIComponent(match[1]) : fallback;
};

// One service file for the employee-loans domain (manager side and self-service).
export const EmployeeLoanService = {
    list: async (institutionId: string, params: { status?: LoanStatus; userId?: string; page?: number; size?: number } = {}): Promise<ILoanPage> => {
        const response = await axios.get(ENDPOINTS.EMPLOYEE_LOANS.LIST(institutionId), {
            ...headers(),
            params: { status: params.status, userId: params.userId, page: params.page ?? 0, size: params.size ?? 20 },
        });
        return response.data;
    },

    get: async (id: string): Promise<IEmployeeLoan> =>
        (await axios.get(ENDPOINTS.EMPLOYEE_LOANS.BY_ID(id), headers())).data,

    create: async (req: ILoanCreateRequest): Promise<IEmployeeLoan> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.CREATE, req, headers())).data,

    approve: async (id: string, req?: ILoanApproveRequest): Promise<IEmployeeLoan> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.APPROVE(id), req ?? null, headers())).data,

    reject: async (id: string, reason: string): Promise<IEmployeeLoan> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.REJECT(id), { reason }, headers())).data,

    cancel: async (id: string): Promise<IEmployeeLoan> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.CANCEL(id), null, headers())).data,

    disburse: async (id: string, payFromAccountId: string | null, date?: string): Promise<IEmployeeLoan> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.DISBURSE(id), { payFromAccountId, date: date || null }, headers())).data,

    repay: async (id: string, req: ILoanRepayRequest): Promise<IEmployeeLoan> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.REPAY(id), req, headers())).data,

    writeOff: async (id: string, reason: string): Promise<IEmployeeLoan> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.WRITE_OFF(id), { reason }, headers())).data,

    holdInstallment: async (installmentId: string): Promise<IEmployeeLoan> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.HOLD_INSTALLMENT(installmentId), null, headers())).data,

    outstanding: async (institutionId: string, asOf?: string): Promise<IOutstandingLoanSummary> =>
        (await axios.get(ENDPOINTS.EMPLOYEE_LOANS.OUTSTANDING(institutionId, asOf), headers())).data,

    reclassRegister: async (institutionId: string): Promise<ILoanInstallment[]> =>
        (await axios.get(ENDPOINTS.EMPLOYEE_LOANS.RECLASS(institutionId), headers())).data,

    reclass: async (institutionId: string, installmentIds: string[]): Promise<ILoanInstallment[]> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.RECLASS(institutionId), { installmentIds }, headers())).data,

    // ---- opening balance: loans paid out while the Accounting module was off ----
    openingBalancePreview: async (institutionId: string): Promise<ILoanOpeningBalancePreview> =>
        (await axios.get(ENDPOINTS.EMPLOYEE_LOANS.OPENING_BALANCE(institutionId), headers())).data,

    postOpeningBalance: async (institutionId: string, req: ILoanOpeningBalanceRequest): Promise<ILoanOpeningBalancePreview> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.OPENING_BALANCE(institutionId), req, headers())).data,

    // ---- disbursement batches ----
    listBatches: async (institutionId: string): Promise<IDisbursementBatch[]> =>
        (await axios.get(ENDPOINTS.EMPLOYEE_LOANS.BATCHES(institutionId), headers())).data,

    getBatch: async (id: string): Promise<IDisbursementBatch> =>
        (await axios.get(ENDPOINTS.EMPLOYEE_LOANS.BATCH(id), headers())).data,

    createBatch: async (req: IDisbursementBatchCreateRequest): Promise<IDisbursementBatch> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.BATCH_CREATE, req, headers())).data,

    disburseBatch: async (id: string, date?: string): Promise<IDisbursementBatch> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.BATCH_DISBURSE(id, date), null, headers())).data,

    cancelBatch: async (id: string): Promise<IDisbursementBatch> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.BATCH_CANCEL(id), null, headers())).data,

    /** Downloads the bank-advice / MFS CSV; resolves with the number of rows the backend excluded (warnings). */
    downloadBatchFile: async (id: string): Promise<number> => {
        try {
            const response = await axios.get(ENDPOINTS.EMPLOYEE_LOANS.BATCH_FILE(id), {
                ...headers(),
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filenameFrom(response.headers?.['content-disposition'], `loan-batch-${id}.csv`));
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return Number(response.headers?.['x-disbursement-warnings'] ?? 0);
        } catch (error) {
            return rethrowBlobError(error);
        }
    },

    // ---- employee self-service ("My Loans") ----
    requestMine: async (req: ILoanSelfRequest): Promise<IEmployeeLoan> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.MY_REQUEST, req, headers())).data,

    listMine: async (institutionId: string, page = 0, size = 20): Promise<ILoanPage> =>
        (await axios.get(ENDPOINTS.EMPLOYEE_LOANS.MY_LIST(institutionId, page, size), headers())).data,

    getMine: async (id: string): Promise<IEmployeeLoan> =>
        (await axios.get(ENDPOINTS.EMPLOYEE_LOANS.MY_BY_ID(id), headers())).data,

    cancelMine: async (id: string): Promise<IEmployeeLoan> =>
        (await axios.post(ENDPOINTS.EMPLOYEE_LOANS.MY_CANCEL(id), null, headers())).data,
};
